import {
  useEffect, useMemo, useRef, useState,
} from 'react';
import PropTypes from 'prop-types';
import { ANIMATIONS, WALK_FRAME_MS } from '../../game/animations';
import {
  SIZE, COLS, ROW_LABELS, idx,
} from './spriteData';

const SCALE = 4;
const BG = '#0D1B2A'; // the real arena background, so contrast is honest

/**
 * Live walk-cycle playback for the selected row.
 *
 * Reads the frame order straight from ANIMATIONS rather than hardcoding
 * 0→1→0→3, so the preview cannot drift from what the game actually plays —
 * a preview that lies is worse than no preview.
 *
 * Judging a walk cycle frame-by-frame is genuinely hard; this is the part of
 * the editor that does the most work.
 */
export default function AnimationPreview({ frames, row }) {
  const canvasRef = useRef(null);
  const [playing, setPlaying] = useState(true);
  const [speed, setSpeed] = useState(WALK_FRAME_MS);
  const [step, setStep] = useState(0);

  const facing = ROW_LABELS[row];

  // Memoised so it is a stable dependency for the draw effect below —
  // recreating the array each render would retrigger the effect every time.
  const order = useMemo(
    () => ANIMATIONS[`walk-${facing}`]?.frames ?? [[0, row]],
    [facing, row],
  );

  useEffect(() => {
    if (!playing) return undefined;
    const timer = setInterval(() => setStep((s) => s + 1), speed);
    return () => clearInterval(timer);
  }, [playing, speed]);

  useEffect(() => {
    const ctx = canvasRef.current.getContext('2d');
    ctx.fillStyle = BG;
    ctx.fillRect(0, 0, SIZE * SCALE, SIZE * SCALE);

    const [col] = order[step % order.length];
    const pixels = frames[row * COLS + col];
    if (!pixels) return;

    for (let y = 0; y < SIZE; y += 1) {
      for (let x = 0; x < SIZE; x += 1) {
        const c = pixels[idx(x, y)];
        if (c) {
          ctx.fillStyle = c;
          ctx.fillRect(x * SCALE, y * SCALE, SCALE, SCALE);
        }
      }
    }
  }, [frames, row, step, order]);

  return (
    <div className="space-y-2">
      <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-400">
        Preview —
        {' '}
        {facing}
      </h3>

      <canvas
        ref={canvasRef}
        width={SIZE * SCALE}
        height={SIZE * SCALE}
        className="rounded-md border border-gray-700"
        style={{ imageRendering: 'pixelated' }}
      />

      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => setPlaying((p) => !p)}
          className="px-2 py-1 text-xs rounded bg-gray-800 text-gray-300 hover:bg-gray-700"
        >
          {playing ? 'Pause' : 'Play'}
        </button>
        <button
          type="button"
          onClick={() => setStep((s) => s + 1)}
          className="px-2 py-1 text-xs rounded bg-gray-800 text-gray-300 hover:bg-gray-700"
        >
          Step
        </button>
      </div>

      <label htmlFor="frame-hold" className="block text-[10px] text-gray-500">
        {`Frame hold: ${speed}ms`}
        <input
          id="frame-hold"
          type="range"
          min="60"
          max="400"
          step="10"
          value={speed}
          onChange={(e) => setSpeed(Number(e.target.value))}
          className="w-full"
        />
      </label>

      <p className="text-[10px] text-gray-500">
        {`Game default is ${WALK_FRAME_MS}ms. Changing this slider previews
        only — edit WALK_FRAME_MS in animations.js to change the game.`}
      </p>
    </div>
  );
}

AnimationPreview.propTypes = {
  frames: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.string)).isRequired,
  row: PropTypes.number.isRequired,
};
